const TRACE_STORAGE_KEY = "chat_trace_enabled";
const DEFAULT_TRACE_LEVEL = "warn";

function canUseBrowserStorage() {
    return typeof window !== "undefined" && !!window.localStorage;
}

export function normalizeChatCustomerNumber(value) {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value).replace(/\D/g, "");
}

export function resolveChatCustomerNumber(payload = {}) {
    if (!payload || typeof payload !== "object") {
        return "";
    }

    if (payload.customerNumber || payload.customernumber || payload.CUSTOMERNUMBER) {
        return payload.customerNumber || payload.customernumber || payload.CUSTOMERNUMBER;
    }

    if (payload.sourcePhone || payload.sourcephone || payload.SOURCEPHONE) {
        return payload.sourcePhone || payload.sourcephone || payload.SOURCEPHONE;
    }

    const sendType = Number(payload.sendType);
    if (sendType === 1 && payload.source) {
        return payload.source;
    }

    if (sendType === 2 && payload.destination) {
        return payload.destination;
    }

    return payload.destination || payload.source || "";
}

export function createChatTraceId(customerNumber = "") {
    const numberTail = normalizeChatCustomerNumber(customerNumber).slice(-4) || "anon";
    const randomPart = Math.random().toString(36).slice(2, 8);
    return `${Date.now()}-${numberTail}-${randomPart}`;
}

export function ensureChatTraceId(payload = {}, customerNumber = "") {
    if (!payload || typeof payload !== "object") {
        return createChatTraceId(customerNumber);
    }

    if (payload.traceId) {
        return payload.traceId;
    }

    const traceId = createChatTraceId(customerNumber || resolveChatCustomerNumber(payload));
    payload.traceId = traceId;
    return traceId;
}

function maskCustomerNumber(value) {
    const digits = normalizeChatCustomerNumber(value);
    if (!digits) {
        return "";
    }

    if (digits.length <= 4) {
        return digits;
    }

    return `${"*".repeat(digits.length - 4)}${digits.slice(-4)}`;
}

export function isChatTraceEnabled() {
    if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
        return true;
    }

    if (!canUseBrowserStorage()) {
        return false;
    }

    try {
        return window.localStorage.getItem(TRACE_STORAGE_KEY) === "1";
    } catch (error) {
        return false;
    }
}

export function setChatTraceEnabled(isEnabled) {
    if (!canUseBrowserStorage()) {
        return;
    }

    try {
        window.localStorage.setItem(TRACE_STORAGE_KEY, isEnabled ? "1" : "0");
    } catch (error) {
        // Storage can fail in strict browser modes; ignore safely.
    }
}

export function chatTrace(stage, details = {}, level = DEFAULT_TRACE_LEVEL) {
    if (!isChatTraceEnabled()) {
        return;
    }

    const logger = typeof console[level] === "function" ? console[level] : console.log;
    const payload = {
        ...details,
        timestamp: new Date().toISOString()
    };

    if (payload.customerNumber) {
        payload.customerNumberMasked = maskCustomerNumber(payload.customerNumber);
    }

    if (payload.activeNumber) {
        payload.activeNumberMasked = maskCustomerNumber(payload.activeNumber);
    }

    logger(`[chat-trace][${stage}]`, payload);
}

export function registerChatTraceBridge() {
    if (typeof window === "undefined") {
        return;
    }

    if (window.chatTrace && window.chatTrace.__chatTraceBridge) {
        return;
    }

    window.chatTrace = {
        __chatTraceBridge: true,
        enable: () => {
            setChatTraceEnabled(true);
            console.info("[chat-trace] enabled");
        },
        disable: () => {
            setChatTraceEnabled(false);
            console.info("[chat-trace] disabled");
        },
        isEnabled: () => isChatTraceEnabled(),
        key: TRACE_STORAGE_KEY,
        help: () => {
            console.info("window.chatTrace.enable() -> production trace on");
            console.info("window.chatTrace.disable() -> production trace off");
            console.info(`localStorage key: ${TRACE_STORAGE_KEY}`);
        }
    };

    chatTrace("bootstrap.ready", {
        environment:
            typeof process !== "undefined" ? process.env.NODE_ENV : "unknown",
        defaultLevel: DEFAULT_TRACE_LEVEL
    }, "warn");
}
